/**
 * MagicBI Design System - useTheme Hook
 * 
 * Enhanced theme hook with additional utilities and helpers
 * for component development and theme management.
 */

import { useContext } from 'react';
import { useTheme as useStyledTheme } from 'styled-components';
import { 
  useTheme as useThemeProvider,
  useThemeMode,
  useThemeColors,
  useThemeSpacing,
  useThemeTypography,
  useThemeShadows,
  useThemeBorders,
  useThemeTransitions,
  useThemeBreakpoints,
} from '../providers/ThemeProvider';
import type { Theme, ThemeMode } from '../design-system/types/theme';

// Main useTheme hook that combines both styled-components and context
export const useTheme = () => {
  const contextTheme = useThemeProvider();
  const styledTheme = useStyledTheme();
  
  return {
    ...contextTheme,
    // Ensure we always have a theme object available
    theme: contextTheme.theme || styledTheme,
  };
};

// Re-export all theme hooks for convenience
export {
  useThemeMode,
  useThemeColors,
  useThemeSpacing,
  useThemeTypography,
  useThemeShadows,
  useThemeBorders,
  useThemeTransitions,
  useThemeBreakpoints,
};

// Advanced theme utilities hook
export const useThemeUtils = () => {
  const { theme, mode, resolvedMode } = useTheme();
  
  return {
    theme,
    mode,
    resolvedMode,
    isDark: resolvedMode === 'dark',
    isLight: resolvedMode === 'light',
    
    // Color utilities
    getColor: (path: string) => {
      const pathArray = path.split('.');
      let value: any = theme.colors;
      
      for (const key of pathArray) {
        value = value?.[key];
        if (value === undefined) break;
      }
      
      return value;
    },
    
    // Spacing utilities
    getSpacing: (size: keyof typeof theme.spacing.semantic.inset) => {
      return theme.spacing.semantic.inset[size];
    },
    
    // Shadow utilities
    getShadow: (elevation: keyof typeof theme.shadows.elevation) => {
      return theme.shadows.elevation[elevation];
    },
    
    // Border radius utilities
    getBorderRadius: (size: keyof typeof theme.borders.radius) => {
      return theme.borders.radius[size];
    },
    
    // Typography utilities
    getTypography: (category: keyof typeof theme.typography, variant: string) => {
      return (theme.typography as any)[category]?.[variant];
    },
    
    // Media query utilities
    getMediaQuery: (breakpoint: keyof typeof theme.breakpoints.values) => {
      return `@media (min-width: ${theme.breakpoints.values[breakpoint]}px)`;
    },
  };
};

// Component-specific theme hooks
export const useButtonTheme = () => {
  const { theme } = useTheme();
  
  return {
    primary: {
      background: theme.colors.interactive.primary,
      backgroundHover: theme.colors.interactive.primaryHover,
      backgroundActive: theme.colors.interactive.primaryActive,
      backgroundDisabled: theme.colors.interactive.primaryDisabled,
      text: theme.colors.text.inverse,
      textDisabled: theme.colors.text.disabled,
      border: theme.colors.interactive.primary,
      borderHover: theme.colors.interactive.primaryHover,
      borderActive: theme.colors.interactive.primaryActive,
      shadow: theme.shadows.component.button.default,
      shadowHover: theme.shadows.component.button.hover,
      radius: theme.borders.semantic.button.md,
      padding: {
        x: theme.spacing.component.button.paddingX.md,
        y: theme.spacing.component.button.paddingY.md,
      },
      typography: theme.typography.button.medium,
      transition: theme.transitions.semantic.interactive.button.all,
    },
    secondary: {
      background: theme.colors.interactive.secondary,
      backgroundHover: theme.colors.interactive.secondaryHover,
      backgroundActive: theme.colors.interactive.secondaryActive,
      backgroundDisabled: theme.colors.surface.primary,
      text: theme.colors.text.primary,
      textDisabled: theme.colors.text.disabled,
      border: theme.colors.border.default,
      borderHover: theme.colors.border.strong,
      borderActive: theme.colors.interactive.primary,
      shadow: theme.shadows.component.button.default,
      shadowHover: theme.shadows.component.button.hover,
      radius: theme.borders.semantic.button.md,
      padding: {
        x: theme.spacing.component.button.paddingX.md,
        y: theme.spacing.component.button.paddingY.md,
      },
      typography: theme.typography.button.medium,
      transition: theme.transitions.semantic.interactive.button.all,
    },
    tertiary: {
      background: theme.colors.interactive.tertiary,
      backgroundHover: theme.colors.interactive.tertiaryHover,
      backgroundActive: theme.colors.interactive.tertiaryActive,
      backgroundDisabled: 'transparent',
      text: theme.colors.text.primary,
      textDisabled: theme.colors.text.disabled,
      border: 'transparent',
      borderHover: 'transparent',
      borderActive: 'transparent',
      radius: theme.borders.semantic.button.md,
      padding: {
        x: theme.spacing.component.button.paddingX.md,
        y: theme.spacing.component.button.paddingY.md,
      },
      typography: theme.typography.button.medium,
      transition: theme.transitions.semantic.interactive.button.all,
    },
  };
};

export const useInputTheme = () => {
  const { theme } = useTheme();
  
  return {
    background: theme.colors.surface.primary,
    backgroundDisabled: theme.colors.surface.secondary,
    border: theme.colors.border.default,
    borderHover: theme.colors.border.strong,
    borderFocus: theme.colors.interactive.primary,
    borderError: theme.colors.error[500],
    text: theme.colors.text.primary,
    textDisabled: theme.colors.text.disabled,
    placeholder: theme.colors.text.tertiary,
    shadow: theme.shadows.component.button.default,
    shadowFocus: theme.shadows.interactive.focus,
    radius: theme.borders.semantic.input.default,
    padding: {
      x: theme.spacing.component.input.paddingX,
      y: theme.spacing.component.input.paddingY,
    },
    typography: theme.typography.body.medium,
    transition: theme.transitions.semantic.interactive.input.border,
  };
};

export const useCardTheme = () => {
  const { theme } = useTheme();
  
  return {
    background: theme.colors.surface.primary,
    border: theme.colors.border.subtle,
    shadow: theme.shadows.component.card.default,
    shadowHover: theme.shadows.component.card.hover,
    radius: theme.borders.semantic.card.default,
    padding: theme.spacing.component.card.padding.md,
    gap: theme.spacing.component.card.gap,
    transition: theme.transitions.semantic.interactive.button.all,
  };
};

export const useModalTheme = () => {
  const { theme } = useTheme();
  
  return {
    backdrop: theme.colors.surface.overlay,
    background: theme.colors.surface.primary,
    border: theme.colors.border.subtle,
    shadow: theme.shadows.component.modal,
    radius: theme.borders.semantic.modal.default,
    padding: theme.spacing.component.modal.padding,
    gap: theme.spacing.component.modal.gap,
    transition: {
      backdrop: theme.transitions.semantic.overlay.backdrop.opacity,
      modal: theme.transitions.semantic.overlay.modal.transform,
    },
  };
};

// Status color utilities
export const useStatusColors = () => {
  const { theme } = useTheme();
  
  return {
    success: {
      background: theme.colors.success[500],
      backgroundLight: theme.colors.success[50],
      border: theme.colors.success[500],
      text: theme.colors.success.text,
      icon: theme.colors.success[500],
    },
    warning: {
      background: theme.colors.warning[500],
      backgroundLight: theme.colors.warning[50],
      border: theme.colors.warning[500],
      text: theme.colors.warning.text,
      icon: theme.colors.warning[500],
    },
    error: {
      background: theme.colors.error[500],
      backgroundLight: theme.colors.error[50],
      border: theme.colors.error[500],
      text: theme.colors.error.text,
      icon: theme.colors.error[500],
    },
    info: {
      background: theme.colors.info[500],
      backgroundLight: theme.colors.info[50],
      border: theme.colors.info[500],
      text: theme.colors.info.text,
      icon: theme.colors.info[500],
    },
  };
};

export default useTheme;
